import PropTypes from "prop-types";
import { Container, Text, Badge, useAuth } from "@artprov/design-system";
import DetailCard from "./DetailCard";
import MatchingCard from "./MatchingCard";
import DocumentoCard from "./DocumentoCard";
import { getBadgeVariantByPercentage } from "./utils/badgeUtils";
import { formatCurrency } from "../../../common/utils/functions/formatCurrency";

const MatchingBancarioDetail = ({
    matchingData,
    emailBancario,
    extractoBancario,
    documento,
    emailBancarioId,
    onUpdateSuccess,
}) => {
    const { variant, label, percentageValue, darkerBgClass } = getBadgeVariantByPercentage(matchingData?.porcentajeTotal);
    const { user } = useAuth();

    const extractoBancarioFields = [
        { label: "Banco", value: extractoBancario?.bancoDescripcion },
        { label: "Número de cuenta", value: extractoBancario?.cuentaNumero },
        { label: "Importe", value: formatCurrency(extractoBancario?.importe) },
        { label: "Fecha Movimiento", value: extractoBancario?.fechaMovimiento },
    ];

    const emailBancarioFields = [
        {
            label: "Fecha del email",
            value: emailBancario?.fechaIngreso,
            editable: true,
            editKey: "fechaIngreso"
        },
        {
            label: "Importe embargo",
            value: formatCurrency(emailBancario?.importe),
            editable: true,
            editKey: "importe"
        },
        {
            label: "Cuenta bancaria",
            value: emailBancario?.cuenta,
            editable: true,
            editKey: "cuenta",
            disabled: true
        },
        {
            label: "Expediente",
            value: emailBancario?.expediente,
            editable: true,
            editKey: "expediente"
        },
        {
            label: "CUIJ",
            value: emailBancario?.cuij,
            editable: true,
            editKey: "cuij"
        },
        {
            label: "Demandante",
            value: emailBancario?.actor,
            editable: true,
            editKey: "actor"
        },
        {
            label: "Demandado",
            value: emailBancario?.demandado,
            editable: true,
            editKey: "demandado"
        },
        {
            label: "Oficio",
            value: emailBancario?.oficio,
            editable: true,
            editKey: "oficio"
        },
        {
            label: "Jurisdicción",
            value: emailBancario?.jurisdiccion,
            editable: true,
            editKey: "jurisdiccion"
        },
        {
            label: "Fuero",
            value: emailBancario?.fuero,
            editable: true,
            editKey: "fuero"
        },
        {
            label: "Juzgado",
            value: emailBancario?.juzgado,
            editable: true,
            editKey: "juzgado"
        },
        {
            label: "Secretaría",
            value: emailBancario?.secretaria,
            editable: true,
            editKey: "secretaria"
        },
        {
            label: "Tipo de medida",
            value: emailBancario?.tipoMedida,
            editable: true,
            editKey: "tipoMedida"
        },
        {
            label: "Número de Trámite",
            value: emailBancario?.nroTramite,
            editable: true,
            editKey: "nroTramite"
        }
    ];

    const documentoFields = [
        { label: "Remitente", value: emailBancario?.remitenteMail },
        { label: "Asunto", value: emailBancario?.asuntoMail },
    ];

    const isEditable = emailBancario?.estadoCodigo === 2 ||
        emailBancario?.estadoCodigo === 3 ||
        emailBancario?.estadoCodigo === 4;

    return (
        <Container className="bg-tertiary-98">
            <div className="flex flex-col gap-2">
                {/* Header con Carátula y Badge */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex flex-col gap-3">
                        <Text variant="h6" className="text-primary-10 mb-1">
                            Carátula {emailBancario?.caratula || "-"}
                        </Text>
                        <div className="flex items-center">
                            <Badge variant={variant} className="font-semibold flex items-center gap-1">
                                {label}
                                <span className={`${darkerBgClass} text-tertiary-100 px-1.5 py-0.5 rounded-full`}>
                                    <span className="font-medium">{percentageValue}</span>
                                </span>
                            </Badge>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-4">
                        <MatchingCard matchingData={matchingData} />

                        <DetailCard title="Información Extracto Bancario" fields={extractoBancarioFields} />

                        <DetailCard
                            title="Información Mail Embargo"
                            fields={emailBancarioFields}
                            editable={isEditable}
                            showBadgeIA={true}
                            emailBancario={emailBancario}
                            userName={user?.preferred_username?.toUpperCase()}
                            onSave={onUpdateSuccess}
                            onCancel={() => console.log("Edición cancelada")}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <DocumentoCard
                            embargoId={emailBancarioId}
                            documentoMetadata={documento}
                            fields={documentoFields}
                        />
                    </div>
                </div>
            </div>
        </Container>
    );
};

MatchingBancarioDetail.propTypes = {
    matchingData: PropTypes.shape({
        idAsociacion: PropTypes.number,
        porcentajeTotal: PropTypes.number,
        campos: PropTypes.arrayOf(
            PropTypes.shape({
                campo: PropTypes.string,
                porcentaje: PropTypes.number,
            })
        ),
    }),
    emailBancario: PropTypes.shape({
        id: PropTypes.number,
        bancoId: PropTypes.number,
        bancoDescripcion: PropTypes.string,
        fechaIngreso: PropTypes.string,
        fechaVencimiento: PropTypes.string,
        expediente: PropTypes.string,
        cuij: PropTypes.string,
        oficio: PropTypes.string,
        jurisdiccion: PropTypes.string,
        idJurisdiccion: PropTypes.number,
        idFuero: PropTypes.number,
        fuero: PropTypes.string,
        idJuzgado: PropTypes.number,
        juzgado: PropTypes.string,
        idSecretaria: PropTypes.number,
        secretaria: PropTypes.string,
        cuenta: PropTypes.string,
        estadoCodigo: PropTypes.number,
        estadoDescripcion: PropTypes.string,
        importe: PropTypes.number,
        caratula: PropTypes.string,
        actor: PropTypes.string,
        demandado: PropTypes.string,
        cuit: PropTypes.string,
        tipoMedida: PropTypes.string,
        observaciones: PropTypes.string,
        remitenteMail: PropTypes.string,
        asuntoMail: PropTypes.string,
        nroTramite: PropTypes.string,
    }),
    extractoBancario: PropTypes.shape({
        id: PropTypes.number,
        bancoId: PropTypes.number,
        bancoDescripcion: PropTypes.string,
        cuentaId: PropTypes.number,
        cuentaNumero: PropTypes.string,
        operacionCodigo: PropTypes.string,
        operacionDescripcion: PropTypes.string,
        tipoOperacion: PropTypes.string,
        numeroComprobante: PropTypes.string,
        fechaMovimiento: PropTypes.string,
        fechaValor: PropTypes.string,
        descripcion: PropTypes.string,
        fechaTraspaso: PropTypes.string,
        cuentaOrigen: PropTypes.string,
        cuitDepositante: PropTypes.string,
        depositante: PropTypes.string,
        codigoOperacionBanco: PropTypes.string,
        sucursalOrigen: PropTypes.string,
        importe: PropTypes.number,
    }),
    documento: PropTypes.shape({
        disponible: PropTypes.bool,
        tieneAdjunto: PropTypes.bool,
        tieneCuerpoMail: PropTypes.bool,
        previewEndpoint: PropTypes.string,
    }),
    emailBancarioId: PropTypes.number,
    onUpdateSuccess: PropTypes.func.isRequired,
};

export default MatchingBancarioDetail;
